import { lazy } from 'react';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import type { ModuleManifest } from '../../lib/host/types';
import { inspectionsKeys } from './queries/keys';

// Review module (P05 §10; W4 row 5; story 5-11b) — the review QUEUE and the
// review SPLIT VIEW, the two surfaces P05 §10 describes.
//
// ── Why its own module, and not a folder inside `capture` ─────────────────
// Because they are different jobs done by different people, and the module
// boundary is what keeps them that way. `capture` is the PERFORMER's surface:
// it writes answers, it holds the autosave/offline machinery, and it is gated
// on `inspections:create`. This is the REVIEWER's surface: it writes no
// answers, it is gated on `inspections:review` (a key the performer does not
// hold at all), and its whole job is to read that work and decide about it.
// Folding them together would put a control an inspector may never press
// inside the bundle an inspector always loads.
//
// There is no `inspections` module to join instead — inspection list/detail are
// still legacy shell pages at HEAD (`/app/items`), which story 5-12 owns
// decomposing. This module is deliberately built so 5-12 can point the
// `in_review` branch of its thin status router straight at `/app/review/:id`.
//
// ── The feature key ───────────────────────────────────────────────────────
// `inspections.core`, the SAME key the backend's review routes enforce
// (`requireFeature('inspections.core')` on both `/review-queue` and the finding
// decision). One key, three planes: the nav row, the route, the endpoint —
// exactly as the reports module records for `reports.designer`.
//
// ── No `entities` / `search` block, so G-21 does not apply ────────────────
// This module declares no new entity type: it lists INSPECTIONS, which is an
// entity the scheduling module already owns and indexes. Declaring it here
// would register a second indexer for one entity, which is worse than none.
export const manifest: ModuleManifest = {
  key: 'review',
  featureKeys: ['inspections.core'],
  nav: [
    {
      key: 'review',
      slot: 'main',
      label: 'Review',
      to: '/app/review',
      icon: ClipboardDocumentCheckIcon,
      // The clamp and the nav row read the SAME key the endpoint gates on, so
      // an inspector never sees a row leading to a 403 (G-13).
      permission: 'inspections:review',
      featureKey: 'inspections.core',
      order: 35,
    },
  ],
  routes: [
    {
      path: '/app/review',
      element: lazy(() => import('./pages/ReviewQueuePage').then((m) => ({ default: m.ReviewQueuePage }))),
      chrome: 'shell',
      permission: 'inspections:review',
      // P16 §6.1 collection family (story 2-146a): EntityListPage + useCursorList
      // + useUrlState, same mechanism SitesPage/TemplateLibraryPage/ItemsListPage
      // compose.
      layoutFamily: 'collection',
    },
    {
      path: '/app/review/:inspectionId',
      element: lazy(() => import('./pages/ReviewDetailPage').then((m) => ({ default: m.ReviewDetailPage }))),
      chrome: 'shell',
      permission: 'inspections:review',
      // P16 §6.1 workflow family (story 2-146b): a single inspection's review
      // split view is a record moving through explicit state (submitted →
      // in_review → approved/changes-requested), the same shape
      // `/app/items/:id` and `/app/sites/:id` land onto in this same story.
      layoutFamily: 'workflow',
    },
  ],
  // P09 §2.1 parity — the capabilities that shipped WITH these endpoints in
  // this same story (agents/src/capabilities/inspections.js). The transition
  // tool is listed because the approve / request-changes edges this surface
  // drives are its edges, and this story added their two refusals to it.
  agentTools: ['inspection_review_queue', 'inspection_finding_decide', 'inspection_transition'],
  queryKeys: inspectionsKeys,
};
